import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { useCallback } from 'react';

const BannerContainer = styled.div`
  background: rgba(253, 242, 205, 0.4);
`;

const BannerContentWrapper = styled.div`
  width: var(--layout-width);
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
  }

  ${mediaQuery.uptoMobile} {
    width: var(--layout-mweb-width);
    margin: 0 auto;
    align-items: center;
    flex-direction: column-reverse;
    padding: ${Spacings.SPACING_8B} 0;
  }
`;

const BannerContent = styled.div`
  width: 33%;
  padding: 51.5px 0;

  ${mediaQuery.uptoTablet} {
    width: 53%;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 20px 0;
    text-align: center;
  }
`;

const Points = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_3B};
  margin: ${Spacings.SPACING_3B} 0 ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_4B} 0;
    gap: 1px;
    justify-content: center;
  }
`;

const Point = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ButtonWrapper = styled.div``;

const StyledButton = styled(Button)`
  width: auto;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const ImageProofWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    justify-content: center;
  }

  ${mediaQuery.uptoMobile} {
    display: contents;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;

  ${mediaQuery.uptoMobile} {
    display: block;
    width: 113px;
  }
`;

const Proof = styled.div`
  position: relative;
  width: 293px;
  background-image: ${({ $imageURL }) =>
    $imageURL && `url(${getImage($imageURL)})`};
  background-color: var(--color-i-white);
  background-size: contain;
  padding: 15px 43px;
  text-align: center;
  border: 1px solid #f4eee0;
  border-radius: 8px;

  ${mediaQuery.uptoMobile} {
    order: -1;
    width: var(--layout-mweb-width);
  }
`;

// need to change bg color
const ProofMarker = styled.div`
  position: absolute;
  width: 14px;
  aspect-ratio: 1;
  border-radius: 50%;
  border-top: 1px solid #f4eee0;
  border-right: 1px solid #f4eee0;
  background-color: var(--color-i-yellow-8);

  :nth-of-type(1) {
    top: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(2) {
    bottom: 20%;
    left: 0;
    transform: translateX(-50%) rotate(45deg);
  }
  :nth-of-type(3) {
    top: 20%;
    right: 0;
    transform: translateX(50%) rotate(225deg);
  }
  :nth-of-type(4) {
    bottom: 20%;
    right: 0;
    transform: translateX(50%) rotate(225deg);
  }
`;

const ScreenWrapper = styled.div`
  &.desktop {
    display: block;
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const Unattempted = ({ block, onClick, clickHandlers }) => {
  const {
    data: [info],
    extraBlockInfo
  } = block;

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    clickHandlers.takeTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { header, subHeader } = extraBlockInfo;
  const breakIndex = subHeader?.indexOf('won') + 3;
  return (
    <BannerContainer>
      <BannerContentWrapper>
        <BannerContent>
          <ScreenWrapper className="desktop">
            <Typography variant="h3">{header}</Typography>
          </ScreenWrapper>
          <ScreenWrapper className="mobile">
            <Typography variant="h4">{header}</Typography>
          </ScreenWrapper>
          <Points>
            <Point>
              <Image
                src="/dst/tick.svg"
                height={20}
                width={20}
                alt="dst-points"
              />
              <ScreenWrapper className="desktop">
                <Typography variant="p2">
                  Just {info.testDuration} minutes{' '}
                </Typography>
              </ScreenWrapper>
              <ScreenWrapper className="mobile">
                <Typography variant="p3">
                  Just {info.testDuration} minutes{' '}
                </Typography>
              </ScreenWrapper>
            </Point>
            <Point>
              <Image
                src="/dst/tick.svg"
                height={20}
                width={20}
                alt="dst-points"
              />
              <ScreenWrapper className="desktop">
                <Typography variant="p2">
                  {info.questionCount} quick questions
                </Typography>
              </ScreenWrapper>
              <ScreenWrapper className="mobile">
                <Typography variant="p3">
                  {info.questionCount} quick questions
                </Typography>
              </ScreenWrapper>
            </Point>
          </Points>
          <ButtonWrapper>
            <ScreenWrapper className="desktop">
              <StyledButton
                label="Attempt test now"
                variant="primary"
                size="large"
                onClick={handleClick}
              />
            </ScreenWrapper>
            <ScreenWrapper className="mobile">
              <StyledButton
                label="Attempt test now"
                variant="primary"
                size="medium"
                onClick={handleClick}
              />
            </ScreenWrapper>
          </ButtonWrapper>
        </BannerContent>
        <ImageProofWrapper>
          <ScreenWrapper className="desktop">
            <ImageWrapper>
              <Image
                src="/dst/trophy-v2.svg"
                height={121}
                width={190}
                alt="dst-trophy"
              />
            </ImageWrapper>
          </ScreenWrapper>
          <ScreenWrapper className="mobile">
            <Image
              src="/dst/trophy-v2-mobile.svg"
              height={72}
              width={113}
              alt="dst-trophy"
            />
          </ScreenWrapper>
          <Proof $imageURL="dst/proofing-bg.svg">
            <ProofMarker />
            <ProofMarker />
            <ProofMarker />
            <ProofMarker />
            <Typography variant="h6" color="#A06740">
              {subHeader?.substring(0, breakIndex)}{' '}
            </Typography>
            <Typography variant="h6" color="#A06740">
              {subHeader?.substring(breakIndex + 1)}{' '}
            </Typography>
          </Proof>
        </ImageProofWrapper>
      </BannerContentWrapper>
    </BannerContainer>
  );
};

export default Unattempted;
